import {
  deviceCredential,
  forgetDeviceCredential,
  storeDeviceCredential,
  suggestedDeviceName,
  type DeviceCredential,
} from './launcher-ipc';
import { isLauncher } from './is-launcher';

/**
 * Registering this machine, and using the credential that comes back.
 *
 * **The seam the API left open and nothing used.** `launcher_device` has been a
 * table since it was written, `GoodbuildDevice` has been a scheme in
 * `apps/api/src/auth.ts`, and `POST /v1/launcher/devices` has issued tokens —
 * and the launcher held none. So the machines list on `/account` stayed empty,
 * every install report carried a null device id, and the launcher signed in
 * with an ordinary browser session that expires like one.
 *
 * ## Why a device token rather than the session
 *
 * They have opposite lifetimes on purpose. A browser session should expire; a
 * launcher that signs a player out every fortnight is a launcher they stop
 * opening. And a device token is revocable one machine at a time, which is what
 * makes a stolen laptop cost one row rather than a password change.
 *
 * ## Why the credential is read synchronously
 *
 * `authorization()` in `api.ts` runs on every request, and reaching across the
 * IPC bridge each time would put a round trip in front of every API call the
 * launcher makes. So the credential is loaded once, into a module-level cache,
 * and read from there. `load()` is called before the first render.
 *
 * ## No cycle with `api.ts`
 *
 * This module never imports `api.ts`. Registering needs an authenticated
 * request, so the caller passes one in — the same shape `reportInstall` uses in
 * `routes/downloads.tsx`, and the reason is the same: `api.ts` imports the
 * synchronous reader below, so an import in the other direction would be a
 * cycle that a bundler resolves by handing one of them an undefined export.
 */

/** What `POST /v1/launcher/devices` answers with. */
interface RegisteredDevice {
  id: string;
  token: string;
  name: string;
}

type Request = <T>(path: string, init: RequestInit) => Promise<T>;

let cached: DeviceCredential | null = null;
let loaded = false;

/**
 * Loads this launcher's credential into the cache.
 *
 * Awaited before the first render, so no request is made against a session when
 * a device token was available. Doing it lazily would mean the first few calls
 * of every run used the wrong credential, which works — both authenticate — and
 * quietly defeats the point: those are the calls made when a session is most
 * likely to have expired.
 */
export async function loadDeviceCredential(): Promise<DeviceCredential | null> {
  if (!isLauncher()) {
    loaded = true;
    return null;
  }

  try {
    cached = await deviceCredential();
  } catch {
    // A shell too old to answer, or a credential file that will not read.
    // Falling back to the session is correct: it authenticates, and the
    // registration is retried on the next run.
    cached = null;
  }

  loaded = true;
  return cached;
}

/**
 * The `Authorization` value for this machine, or null.
 *
 * Synchronous by design — see the header. Null before `loadDeviceCredential`
 * has run, which makes `api.ts` fall back to the session rather than block.
 */
export function deviceAuthorization(): string | null {
  return cached === null ? null : `GoodbuildDevice ${cached.token}`;
}

export async function discardDeviceCredential(): Promise<void> {
  if (cached === null) return;
  cached = null;
  await forgetDeviceCredential().catch(() => undefined);
}

/** Whether this launcher has registered itself. */
export function isRegistered(): boolean {
  return cached !== null;
}

export function deviceName(): string | null {
  return cached?.name ?? null;
}

/**
 * Registers this machine if it has not been registered.
 *
 * **Uses the session deliberately.** A launcher with no device token has only a
 * session, and this is the one call that trades one for the other — which is
 * why `POST /v1/launcher/devices` is the single endpoint on that router reached
 * by a caller that may hold no device credential.
 *
 * Failures are swallowed. A launcher that cannot register still downloads,
 * installs and plays perfectly well on its session; what it loses is a row in a
 * list and a longer-lived credential, neither of which is worth an error on a
 * screen about games. It tries again on the next run.
 */
export async function ensureRegistered(request: Request): Promise<void> {
  if (!isLauncher()) return;
  if (!loaded) await loadDeviceCredential();
  if (cached !== null) return;

  try {
    const name = await suggestedDeviceName().catch(() => 'This machine');

    const registered = await request<RegisteredDevice>('/launcher/devices', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });

    if (typeof registered?.token !== 'string' || registered.token === '') return;

    const credential: DeviceCredential = {
      device_id: registered.id,
      token: registered.token,
      name: registered.name ?? name,
    };

    /*
     * Stored before it is cached.
     *
     * The token is shown once and cannot be recovered, so caching it first and
     * failing to write would leave this run authenticating with a credential
     * that vanishes when the launcher closes — and a row on the player's
     * account that nothing can ever name again.
     */
    await storeDeviceCredential(credential);
    cached = credential;
  } catch {
    // See above.
  }
}

/**
 * Revokes this machine, then forgets it.
 *
 * **That order, and it matters.** Forgetting first would leave a live
 * credential on the player's account with nothing able to name it — the exact
 * orphan the machines list exists to prevent. The local file is cleared even if
 * the revoke fails, because the alternative is a launcher that has signed out
 * while still holding a working credential.
 */
export async function revokeThisDevice(request: Request): Promise<void> {
  if (!isLauncher()) return;

  const credential = cached;
  cached = null;

  if (credential === null) return;

  try {
    await request(`/launcher/devices/${encodeURIComponent(credential.device_id)}`, {
      method: 'DELETE',
    });
  } catch {
    // Already revoked from another machine, or offline. The local half still
    // happens: a signed-out launcher must not keep a usable credential.
  }

  try {
    await forgetDeviceCredential();
  } catch {
    // Nothing further to do. The next run reads a credential the server has
    // already refused and registers again.
  }
}
